import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useBusinessPlan } from '../../hooks/useBusinessPlan';
import LoadingScreen from '../shared/LoadingScreen';
import { callOpenAI } from '../../lib/functions';
import type { BusinessPlan } from '../../types/businessPlan';

const TOOL_CARDS = [
  { icon: '🎤', label: 'Pitch Deck' },
  { icon: '📊', label: 'Dashboard' },
  { icon: '🌐', label: 'Website' },
  { icon: '📢', label: 'Marketing' },
  { icon: '📦', label: 'Catalogue' },
];

type Phase = 'text1' | 'text2' | 'morph' | 'cta';

/**
 * Collapse the stage responses + homework into a plain-text brief the LLM
 * can read to write a celebratory summary.
 */
function buildPlanBrief(plan: BusinessPlan): string {
  const lines: string[] = [];
  lines.push(`Business name: ${plan.businessName || 'Untitled'}`);
  lines.push(`Founder: ${plan.explorerName}`);

  const stageOrder: Array<[keyof BusinessPlan['stageResponses'], string]> = [
    ['ideaExplosion', 'Stage 1 — Origin Story'],
    ['problemSolver', 'Stage 2 — The Struggle Is Real'],
    ['customerDetective', "Stage 3 — From 'Ugh!' to 'Aha!'"],
    ['mvpMadness', 'Stage 4 — Who Will Buy This?'],
    ['moneyMoves', 'Stage 5 — Show Me The Money'],
    ['competitionStation', 'Stage 6 — The Game Plan'],
  ];

  for (const [key, label] of stageOrder) {
    const answers = plan.stageResponses?.[key];
    if (answers && Object.keys(answers).length > 0) {
      lines.push(`\n${label}:`);
      Object.values(answers).forEach((a) => {
        const trimmed = String(a).trim();
        if (trimmed) lines.push(`- ${trimmed}`);
      });
    }
  }

  if (plan.elevatorPitchSolution) {
    lines.push(`\nElevator pitch: ${plan.elevatorPitchSolution}`);
  }
  if (plan.profitCalculation) {
    lines.push(
      `\nPricing: selling ${plan.profitCalculation.sellingPrice}, cost ${plan.profitCalculation.costToMake}.`,
    );
  }

  return lines.join('\n').slice(0, 3500);
}

const SYSTEM_PROMPT = `You are KoKo, a warm, cheeky co-founder fox mentor for a young founder
aged 8–16. The founder has just completed all 6 stages of their business
plan. Write a short (2–3 sentences) celebratory note that:
- Names one specific strength from their responses.
- Names one specific thing to sharpen next.
- Ends on an energising forward nudge.
Use British English. One emoji maximum. Never say "kindness". Plain prose,
no bullet points.`;

export default function BridgeScreen() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { plan, loading } = useBusinessPlan(planId);
  const [phase, setPhase] = useState<Phase>('text1');

  const [summary, setSummary] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const calledRef = useRef(false);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    timers.push(setTimeout(() => setPhase('text2'), 2500));
    timers.push(setTimeout(() => setPhase('morph'), 5000));
    timers.push(setTimeout(() => setPhase('cta'), 7000));
    return () => timers.forEach(clearTimeout);
  }, []);

  // Kick off the LLM summary once the plan arrives. Guarded so it only
  // runs once per mount (the onSnapshot stream can emit repeatedly).
  useEffect(() => {
    if (!plan || calledRef.current) return;
    calledRef.current = true;
    setSummaryLoading(true);
    callOpenAI({
      systemPrompt: SYSTEM_PROMPT,
      prompt: buildPlanBrief(plan),
    })
      .then((res) => {
        const text = res.data?.responseText?.trim();
        if (text) setSummary(text);
        else setSummaryError(true);
      })
      .catch((err) => {
        console.error('Plan summary failed:', err);
        setSummaryError(true);
      })
      .finally(() => setSummaryLoading(false));
  }, [plan]);

  if (loading) return <LoadingScreen />;

  const businessName = plan?.businessName || 'your business';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-b from-brand-600 to-brand-800 px-4">
      <div className="w-full max-w-lg text-center">
        <AnimatePresence mode="wait">
          {phase === 'text1' && (
            <motion.div
              key="text1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-5xl">🚀</span>
              <h1 className="mt-4 text-2xl font-bold text-white md:text-3xl">
                Your business plan for{' '}
                <span className="text-yellow-300">{businessName}</span> is
                ready.
              </h1>
            </motion.div>
          )}

          {phase === 'text2' && (
            <motion.div
              key="text2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-5xl">💭</span>
              <h2 className="mt-4 text-xl font-semibold text-white/90 md:text-2xl">
                But a plan without action is just a dream.
              </h2>
            </motion.div>
          )}

          {phase === 'morph' && (
            <motion.div
              key="morph"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="mb-6 text-lg font-medium text-white/80">
                Your stages are becoming tools…
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                {TOOL_CARDS.map((card, i) => (
                  <motion.div
                    key={card.label}
                    initial={{ scale: 0, rotate: -20 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{
                      delay: i * 0.15,
                      type: 'spring',
                      stiffness: 200,
                      damping: 15,
                    }}
                    className="flex h-20 w-20 flex-col items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm md:h-24 md:w-24"
                  >
                    <span className="text-2xl md:text-3xl">{card.icon}</span>
                    <span className="mt-1 text-[10px] font-medium text-white/90 md:text-xs">
                      {card.label}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {phase === 'cta' && (
            <motion.div
              key="cta"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-6xl">🏗️</span>
              <h2 className="mt-4 text-2xl font-bold text-white md:text-3xl">
                Time to build it.
              </h2>
              <p className="mt-2 text-white/70">
                Your Business HQ has 5 powerful tools waiting.
              </p>

              {/* KoKo's take — LLM-generated summary of the completed plan */}
              <div className="mt-6 rounded-2xl border border-white/20 bg-white/10 p-4 text-left backdrop-blur-sm">
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-xl">🦊</span>
                  <span className="text-sm font-semibold uppercase tracking-wider text-yellow-300">
                    KoKo's take
                  </span>
                </div>
                {summaryLoading && (
                  <div className="flex items-center gap-1 py-2">
                    {[0, 150, 300].map((delay) => (
                      <span
                        key={delay}
                        className="inline-block h-2 w-2 animate-pulse rounded-full bg-white/70"
                        style={{ animationDelay: `${delay}ms` }}
                      />
                    ))}
                  </div>
                )}
                {!summaryLoading && summary && (
                  <p className="text-sm leading-relaxed text-white">{summary}</p>
                )}
                {!summaryLoading && !summary && summaryError && (
                  <p className="text-sm italic text-white/70">
                    KoKo is taking a breath — you'll get a full summary in your HQ.
                  </p>
                )}
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(`/hq/${planId}`)}
                className="mt-8 inline-flex min-h-[44px] items-center gap-2 rounded-2xl bg-yellow-400 px-8 py-4 text-lg font-bold text-gray-900 shadow-lg transition-colors hover:bg-yellow-300"
              >
                Let&apos;s Build It 🚀
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
